import { StatisticsPage } from 'src/pages/statistics/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppState } from 'src/store/store';
import statisticsApiService from 'src/api/services/statisticsApiService';
import { Dayjs } from 'dayjs';

const initialState: StatisticsPage = {
    requestId: undefined,
    status: 'initial',
    period: null,
    currentState: null
};

export const submitRequest = createAsyncThunk<void, void, { state: AppState }>(
    'statistics/calculate',
    async (_, { getState }) => {
        const period = selectStatisticsPeriod(getState());
        if (!period || !period.startDate || !period.finishDate) {
            throw new Error('Не задан период');
        }
        return await statisticsApiService.calculateStatistics({
            startDate: period.startDate.format('YYYY-MM-DD'),
            finishDate: period.finishDate.format('YYYY-MM-DD')
        });
    }
);

export const loadStatistics = createAsyncThunk('statistics/load', () => {
    return statisticsApiService.loadStatistics();
});

const slice = createSlice({
    name: 'statistics',
    initialState,
    reducers: {
        reset: () => initialState,
        periodChanged: (state, action: PayloadAction<[Dayjs | null, Dayjs | null]>) => {
            state.period = {
                startDate: action.payload[0],
                finishDate: action.payload[1]
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(submitRequest.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'submit';
            })
            .addCase(submitRequest.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(submitRequest.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            })
            .addCase(loadStatistics.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
                state.currentState = null;
            })
            .addCase(loadStatistics.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.currentState = action.payload;
                }
            })
            .addCase(loadStatistics.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                }
            });
    }
});

export const { reset, periodChanged } = slice.actions;

const selectStatisticsPageState = (state: AppState) => state.statistics;

export const selectStatisticsStatus = (state: AppState) => selectStatisticsPageState(state).status;

export const selectStatisticsPeriod = (state: AppState) => selectStatisticsPageState(state).period;

export const selectStatisticsCurrentState = (state: AppState) => selectStatisticsPageState(state).currentState;

export default slice.reducer;
